
'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Icons } from '@/components/icons';
import Link from 'next/link';
import { useAuth } from '@/context/auth-context';
import { useRouter } from 'next/navigation';
import { useToast } from '@/hooks/use-toast';
import { useState, useEffect } from 'react';
import { FirebaseError } from 'firebase/app';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

const loginSchema = z.object({
  email: z.string().email('Debe ser un correo electrónico válido.'),
  password: z.string().min(1, 'La contraseña es obligatoria.'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const { signIn, user, resetPassword } = useAuth();
  const router = useRouter();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetOpen, setIsResetOpen] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    if (user) {
      router.push('/dashboard');
    }
  }, [user, router]);


  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast({
        title: 'Inicio de sesión exitoso',
        description: 'Bienvenido de nuevo.',
      });
      router.push('/dashboard');
    } catch (error: any) {
      console.error(error);
      let description = 'Ocurrió un error. Por favor, inténtalo de nuevo.';
      if (error instanceof FirebaseError && error.code === 'auth/invalid-credential') {
        description = 'El correo electrónico o la contraseña son incorrectos. Por favor, inténtalo de nuevo.'
      }
        
      toast({
        variant: 'destructive',
        title: 'Error al iniciar sesión',
        description: description,
      });
    } finally {
        setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
      if (!resetEmail) {
          toast({ variant: 'destructive', title: 'Email requerido' });
          return;
      }
      setIsResetLoading(true);
      try {
          await resetPassword(resetEmail);
          toast({ 
              title: 'Correo enviado', 
              description: 'Se ha enviado un enlace a su casilla. Revise también la carpeta de Spam.' 
          });
          setIsResetOpen(false);
          setResetEmail('');
      } catch (error: any) {
          console.error("Reset Error:", error);
          let message = 'No se pudo enviar el correo de recuperación.';
          
          if (error instanceof FirebaseError) {
              if (error.code === 'auth/user-not-found') {
                  message = 'No existe un usuario registrado con este correo.';
              } else if (error.code === 'auth/invalid-email') {
                  message = 'El formato del correo es inválido.';
              } else if (error.code === 'auth/too-many-requests') {
                  message = 'Demasiados intentos. Intente más tarde.';
              }
          }
          
          toast({ 
              variant: 'destructive', 
              title: 'Fallo en Recuperación', 
              description: message 
          });
      } finally {
          setIsResetLoading(false);
      }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40">
      <Card className="w-full max-w-sm shadow-2xl border-primary/20">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <Icons.logo className="h-14 w-14 text-primary" />
          </div>
          <CardTitle className="text-2xl font-headline font-black uppercase text-accent">Iniciar Sesión</CardTitle>
          <CardDescription className="font-bold">
            Ingresa a tu cuenta industrial de TimberFlow.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[10px] font-black uppercase">Correo Electrónico</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="tu@correo.com"
                        className="font-bold"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                        <FormLabel className="text-[10px] font-black uppercase">Contraseña</FormLabel>
                        <Dialog open={isResetOpen} onOpenChange={setIsResetOpen}>
                            <DialogTrigger asChild>
                                <Button variant="link" className="px-0 h-auto text-[10px] font-black uppercase tracking-tighter text-muted-foreground hover:text-primary">
                                    ¿Olvidó su clave?
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md border-2 border-primary">
                                <DialogHeader>
                                    <DialogTitle className="uppercase font-black text-accent">Recuperar Acceso</DialogTitle>
                                    <DialogDescription className="font-bold text-gray-900">
                                        Ingrese su correo electrónico y le enviaremos un enlace oficial de Google para restablecer su contraseña.
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="py-4 space-y-4">
                                    <div className="space-y-2">
                                        <FormLabel className="text-[10px] font-black uppercase text-muted-foreground">Correo Registrado en el Sistema</FormLabel>
                                        <Input 
                                            placeholder="ejemplo@rollizos.cl" 
                                            value={resetEmail} 
                                            onChange={(e) => setResetEmail(e.target.value)}
                                            className="font-bold h-12"
                                        />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setIsResetOpen(false)} className="font-bold">CANCELAR</Button>
                                    <Button onClick={handleResetPassword} disabled={isResetLoading} className="font-black uppercase tracking-widest bg-accent text-accent-foreground shadow-lg">
                                        {isResetLoading ? 'ENVIANDO...' : 'ENVIAR ENLACE'}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" className="font-mono h-12" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full font-black uppercase tracking-widest h-12 shadow-lg" disabled={isLoading}>
                {isLoading ? 'INGRESANDO...' : 'INGRESAR AL SISTEMA'}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
            <div className="text-[10px] font-bold text-muted-foreground uppercase text-center">
                ¿No tiene acceso? Contacte al Administrador.
            </div>
        </CardFooter>
      </Card>
    </div>
  );
}
